import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Status from 'apollo-react/components/Status';
import Typography from 'apollo-react/components/Typography';
import StatusDotSolid from 'apollo-react-icons/StatusDotSolid';
import qs from 'query-string';
import React from 'react';

import Link from '../../components/Link';
import { ENTITY_DISPLAY } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { formatMoment } from '../../utils/dateUtils';

const statusLabels = {
  INWORKS: 'Inworks',
  PRERELEASED: 'Pre-Released',
  PREEXPIRE: 'Preexpire',
  REVISED: 'Revised',
  CURRENT: 'Current',
  EXPIRED: 'Expired',
  CLOSED: 'Closed',
  VOID: 'Void',
  ABORTED: 'Aborted',
  RELEASED: 'Released',
  EFFECTIVE: 'Effective',
  ARCHIVED: 'Archived',
};

const statusVariants = {
  INWORKS: 'warning',
  PRERELEASED: 'positive',
  PREEXPIRE: 'positive',
  REVISED: 'positive',
  CURRENT: 'positive',
  EXPIRED: 'negative',
  CLOSED: 'negative',
  VOID: 'negative',
  ABORTED: 'negative',
  RELEASED: 'positive',
  EFFECTIVE: 'positive',
  ARCHIVED: 'negative',
};

const useStyles = makeStyles(({ palette }) => ({
  container: {
    padding: '32px 0',
  },
  date: {
    color: palette?.grey[600],
    paddingTop: 3,
  },
  property: {
    color: palette?.grey[800],
  },
  linkContainer: {
    zIndex: 10,
    position: 'relative',
  },
  link: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 1.5,
    color: 'black',
    '&:hover, &:hover::selection, &:focus, &:focus::selection': {
      color: palette?.primary?.main,
    },
    '&:focus': {
      textDecoration: 'underline',
    },
  },
  statusIcon: {
    width: 10,
    height: 11,
  },
}));

const SearchResult = ({
  entityType,
  formKey,
  itmId,
  modifiedDate,
  recordNumber,
  status,
  title,
  translatedEntityName,
  phase,
}) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const queryString = qs.stringify({
    entityType,
    formKey,
    itmId,
  });

  return (
    <Grid className={classes.container} container>
      <Grid item xs={2}>
        <Typography className={classes.date} variant="subtitle1" component="h4">
          {formatMoment(modifiedDate)}
        </Typography>
      </Grid>
      <Grid container item xs={10} spacing={2} direction="column">
        <Grid item className={classes.linkContainer}>
          <Link
            className={classes.link}
            to={{
              pathname: ENTITY_DISPLAY,
              search: `?${queryString}`,
            }}
          >
            {`${recordNumber}: ${title}`}
          </Link>
        </Grid>
        <Grid container item spacing={5} alignItems="baseline">
          <Grid item>
            <Typography className={classes.property} variant="body2">
              <strong>{`${translateString('Entity')}: `}</strong>
              {translatedEntityName}
            </Typography>
          </Grid>
          <Grid item>
            <Typography className={classes.property} variant="body2">
              <strong>{`${translateString('Phase')}: `}</strong>
              {translateString(phase)}
            </Typography>
          </Grid>
          {status && (
            <Grid item xs={3}>
              <Grid container alignItems="center">
                <Grid item>
                  <Typography className={classes.property} variant="body2">
                    <strong>{`${translateString('Status')}: `}</strong>
                  </Typography>
                </Grid>
                <Grid item>
                  <Status
                    classes={{
                      text: classes.property,
                      icon: classes.statusIcon,
                    }}
                    variant={statusVariants[status.toUpperCase()]}
                    label={translateString(statusLabels[status.toUpperCase()] || status)}
                    icon={StatusDotSolid}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SearchResult;
